import type {
  DocumentDto,
  DocumentValidationReviewDto,
  DocumentValueDto,
  ModuleDto,
  MyWorkSiteDto,
} from '@app/contracts';
import {
  AlertTriangle,
  Building2,
  ChevronDown,
  ChevronUp,
  Layers,
  Plus,
  Save,
  Trash2,
  Wand2,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useIsoModulesForSiteIds } from '../../../modules/hooks/useModules.js';
import { useMySites } from '../../../progression/hooks/useProgression.js';
import {
  useCreateDocumentValue,
  useDeleteDocumentValue,
  useUpdateDocumentValue,
} from '../../hooks/useDocuments.js';
import { DocumentRightsButton } from '../DocumentRightsButton.js';
import { DocumentSubmitButton } from '../DocumentSubmitButton.js';
import { InlineDocumentValueValidation } from '../InlineDocumentValueValidation.js';
import {
  REGISTRY_CATEGORIES,
  REGISTRY_IMPACTS,
  type RegistryRow,
  createEmptyRow,
  formatCategorieLabel,
  formatImpactLabel,
  normalizeRegistryRow,
  rowToSerializedData,
  validateRowStrict,
} from './RegistryModel.js';

interface Props {
  document: DocumentDto;
  values: DocumentValueDto[];
  /** Ouvre la modale des droits (admin) si l’URL contient `openDocumentRights` (ex. lien parcours phase 2). */
  autoOpenDocumentRights?: boolean;
  readOnly?: boolean;
  changedValueIds?: Set<string>;
  validationReview?: DocumentValidationReviewDto;
}

function toInputDate(iso: string): string {
  return iso.length >= 10 ? iso.slice(0, 10) : iso;
}

const BADGE_BASE =
  'inline-flex max-w-full shrink-0 items-center gap-0.5 rounded-full border px-2 py-0.5 text-[11px] font-medium leading-none';

function categoryBadgeClass(categorie: string): string {
  switch (categorie) {
    case 'interne':
      return 'border-violet-200 bg-violet-50 text-violet-800';
    case 'externe':
      return 'border-sky-200 bg-sky-50 text-sky-800';
    case 'regulateur':
      return 'border-amber-200 bg-amber-50 text-amber-900';
    default:
      return 'border-slate-200 bg-slate-50 text-slate-600';
  }
}

function impactBadgeClass(impact: string): string {
  switch (impact) {
    case 'eleve':
      return 'border-rose-200 bg-rose-50 text-rose-800';
    case 'moyen':
      return 'border-amber-200 bg-amber-50 text-amber-800';
    case 'faible':
      return 'border-emerald-200 bg-emerald-50 text-emerald-800';
    default:
      return 'border-slate-200 bg-slate-50 text-slate-500';
  }
}

function sitesLabel(count: number): string {
  if (count === 0) return 'Aucun site';
  if (count === 1) return '1 site';
  return `${count} sites`;
}

function modulesLabel(count: number): string {
  if (count === 0) return 'Aucun module';
  if (count === 1) return '1 module';
  return `${count} modules`;
}

export function ToolNeedsWait({
  document,
  values,
  autoOpenDocumentRights,
  readOnly = false,
  changedValueIds,
  validationReview,
}: Props) {
  const { data: workSites, isLoading: sitesLoading } = useMySites();
  const workSiteIds = useMemo(() => workSites?.map((s) => s.siteId) ?? [], [workSites]);
  const { modules: moduleChoices, isLoading: modulesLoading } =
    useIsoModulesForSiteIds(workSiteIds);

  const [rows, setRows] = useState<RegistryRow[]>(() =>
    values.map((v) =>
      normalizeRegistryRow({ id: v.id, ...(v.serialization as Record<string, unknown>) }, v),
    ),
  );
  const [openAccordion, setOpenAccordion] = useState<number | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSavingAll, setIsSavingAll] = useState(false);
  const [saveAllStatus, setSaveAllStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  const createValue = useCreateDocumentValue(document.id);
  const updateValue = useUpdateDocumentValue(document.id);
  const deleteValue = useDeleteDocumentValue(document.id);

  useEffect(() => {
    setRows(
      values.map((v) =>
        normalizeRegistryRow({ id: v.id, ...(v.serialization as Record<string, unknown>) }, v),
      ),
    );
    setHasUnsavedChanges(false);
  }, [values]);

  useEffect(() => {
    const h = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [hasUnsavedChanges]);

  const today = new Date().toISOString().slice(0, 10);
  const nextYear = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

  const DEMO_ROWS: RegistryRow[] = [
    {
      id: `demo-${Date.now()}-1`,
      designation: "Clients / Donneurs d'ordre",
      categorie: 'externe',
      besoinsEtAttentes:
        'Produits et services conformes aux exigences contractuelles.\nDelais respectés, communication fluide.',
      exigences: 'Respect des cahiers des charges et des normes applicables (ISO 9001).',
      impact: 'eleve',
      pilote: 'Direction commerciale',
      modalitesPriseEnCompte:
        'Revues de contrat, enquêtes de satisfaction annuelles, suivi des réclamations.',
      dateDerniereRevue: today,
      dateProchaineRevue: nextYear,
      commentaires: '',
      sites: [],
      modules: [],
      dateCreated: new Date().toISOString(),
      dateUpdated: new Date().toISOString(),
    },
    {
      id: `demo-${Date.now()}-2`,
      designation: 'Personnel',
      categorie: 'interne',
      besoinsEtAttentes:
        'Conditions de travail sûres et épanouissantes.\nFormation continue et évolution professionnelle.',
      exigences: 'Respect de la législation sociale, conventions collectives.',
      impact: 'eleve',
      pilote: 'Direction RH',
      modalitesPriseEnCompte:
        "Entretiens annuels, plan de formation, affichage réglementaire, réunions d'équipe.",
      dateDerniereRevue: today,
      dateProchaineRevue: nextYear,
      commentaires: '',
      sites: [],
      modules: [],
      dateCreated: new Date().toISOString(),
      dateUpdated: new Date().toISOString(),
    },
    {
      id: `demo-${Date.now()}-3`,
      designation: 'Fournisseurs et prestataires',
      categorie: 'externe',
      besoinsEtAttentes: 'Relations partenariales durables, paiements dans les délais.',
      exigences: 'Conformité aux exigences qualité transmises, réponse aux audits fournisseurs.',
      impact: 'moyen',
      pilote: 'Responsable achats',
      modalitesPriseEnCompte:
        'Évaluation annuelle des fournisseurs, grille de qualification, audits ponctuels.',
      dateDerniereRevue: today,
      dateProchaineRevue: nextYear,
      commentaires: '',
      sites: [],
      modules: [],
      dateCreated: new Date().toISOString(),
      dateUpdated: new Date().toISOString(),
    },
    {
      id: `demo-${Date.now()}-4`,
      designation: 'Autorités réglementaires',
      categorie: 'regulateur',
      besoinsEtAttentes: 'Conformité légale et réglementaire, coopération lors des contrôles.',
      exigences:
        'Respect des réglementations en vigueur (sécurité, environnement, normes sectorielles).',
      impact: 'eleve',
      pilote: 'Responsable qualité',
      modalitesPriseEnCompte:
        'Veille réglementaire, déclarations obligatoires, audits de conformité.',
      dateDerniereRevue: today,
      dateProchaineRevue: nextYear,
      commentaires: '',
      sites: [],
      modules: [],
      dateCreated: new Date().toISOString(),
      dateUpdated: new Date().toISOString(),
    },
    {
      id: `demo-${Date.now()}-5`,
      designation: 'Direction / Actionnaires',
      categorie: 'interne',
      besoinsEtAttentes: 'Rentabilité, croissance maîtrisée, image de marque positive.',
      exigences: 'Atteinte des objectifs stratégiques, reporting régulier.',
      impact: 'eleve',
      pilote: 'Direction générale',
      modalitesPriseEnCompte:
        'Revues de direction trimestrielles, tableaux de bord, plan stratégique annuel.',
      dateDerniereRevue: today,
      dateProchaineRevue: nextYear,
      commentaires: '',
      sites: [],
      modules: [],
      dateCreated: new Date().toISOString(),
      dateUpdated: new Date().toISOString(),
    },
  ];

  const fillWithDemoData = () => {
    const siteRefs =
      workSites?.map((s) => ({
        id: s.siteId,
        name: s.siteName,
        city: s.city ?? undefined,
        country: s.country ?? undefined,
      })) ?? [];
    const moduleRefs = moduleChoices.map((m) => ({ id: m.id, name: m.name, code: m.code }));
    if (siteRefs.length === 0) {
      alert(
        'Aucun site n’est assigné à votre compte : les lignes démo n’auront pas de site. Affectez-vous à au moins un site, ou cochez les sites après coup.',
      );
    }
    if (moduleRefs.length === 0 && workSiteIds.length > 0 && !modulesLoading) {
      alert(
        'Aucun module ISO sur vos sites : les cases modules restent vides. Assignez des modules dans la page Modules.',
      );
    }
    setRows(
      DEMO_ROWS.map((r) => ({
        ...r,
        sites: siteRefs,
        modules: moduleRefs,
      })),
    );
    setOpenAccordion(null);
    setHasUnsavedChanges(true);
  };

  const demoButtonDisabled = sitesLoading || (workSiteIds.length > 0 && modulesLoading);

  const updateRow = (index: number, partial: Partial<RegistryRow>) => {
    setRows((prev) =>
      prev.map((row, i) =>
        i === index ? { ...row, ...partial, dateUpdated: new Date().toISOString() } : row,
      ),
    );
    setHasUnsavedChanges(true);
  };

  const addRow = () => {
    setRows((prev) => {
      const next = [...prev, createEmptyRow()];
      setOpenAccordion(next.length - 1);
      return next;
    });
    setHasUnsavedChanges(true);
  };

  const toggleRowSite = (index: number, site: MyWorkSiteDto, selected: boolean) => {
    setRows((prev) => {
      const row = prev[index];
      if (!row) return prev;
      const nextSites = selected
        ? [
            ...row.sites.filter((s) => s.id !== site.siteId),
            {
              id: site.siteId,
              name: site.siteName,
              city: site.city ?? undefined,
              country: site.country ?? undefined,
            },
          ]
        : row.sites.filter((s) => s.id !== site.siteId);
      return prev.map((r, i) =>
        i === index ? { ...r, sites: nextSites, dateUpdated: new Date().toISOString() } : r,
      );
    });
    setHasUnsavedChanges(true);
  };

  const toggleRowModule = (index: number, mod: ModuleDto, selected: boolean) => {
    setRows((prev) => {
      const row = prev[index];
      if (!row) return prev;
      const nextModules = selected
        ? [
            ...row.modules.filter((m) => m.id !== mod.id),
            { id: mod.id, name: mod.name, code: mod.code },
          ]
        : row.modules.filter((m) => m.id !== mod.id);
      return prev.map((r, i) =>
        i === index ? { ...r, modules: nextModules, dateUpdated: new Date().toISOString() } : r,
      );
    });
    setHasUnsavedChanges(true);
  };

  const saveAllRows = async () => {
    if (isSavingAll) return;
    const allErrors = rows.flatMap((row, i) =>
      validateRowStrict(row, {
        requireSites: true,
        skipContentRules: !document.strictValidation,
      }).map((e) => `Ligne ${i + 1} : ${e}`),
    );
    if (allErrors.length > 0) {
      alert(allErrors.join('\n'));
      return;
    }
    setIsSavingAll(true);
    setSaveAllStatus('saving');
    try {
      const updatedRows = [...rows];
      for (let i = 0; i < updatedRows.length; i++) {
        const row = updatedRows[i];
        if (!row) continue;
        const serialization = rowToSerializedData(row);
        const existingId = typeof row.id === 'string' && row.id.length === 36 ? row.id : null;
        if (existingId) {
          await updateValue.mutateAsync({
            valueId: existingId,
            input: {
              serialization,
              siteIds: row.sites.map((s) => s.id),
              moduleIds: row.modules.map((m) => m.id),
            },
          });
        } else {
          const created = await createValue.mutateAsync({
            serialization,
            siteIds: row.sites.map((s) => s.id),
            moduleIds: row.modules.map((m) => m.id),
          });
          updatedRows[i] = { ...row, id: (created as { id: string }).id };
        }
      }
      setRows(updatedRows);
      setHasUnsavedChanges(false);
      setSaveAllStatus('saved');
      setTimeout(() => setSaveAllStatus('idle'), 2500);
    } catch {
      setSaveAllStatus('error');
      setTimeout(() => setSaveAllStatus('idle'), 3000);
    } finally {
      setIsSavingAll(false);
    }
  };

  const deleteRow = async (index: number) => {
    const row = rows[index];
    if (!row) return;
    const existingId = typeof row.id === 'string' && row.id.length === 36 ? row.id : null;
    if (existingId) {
      if (!confirm('Supprimer cette ligne ?')) return;
      try {
        await deleteValue.mutateAsync(existingId);
      } catch {
        alert('Erreur lors de la suppression.');
        return;
      }
    }
    setRows((prev) => prev.filter((_, i) => i !== index));
    if (openAccordion === index) setOpenAccordion(null);
  };

  return (
    <div>
      <div className="mb-4 flex items-start justify-between rounded-xl border border-slate-200 bg-slate-50 p-4">
        <div>
          <h1 className="text-lg font-bold text-slate-900">{document.name}</h1>
          <p className="mt-0.5 text-xs text-slate-500">
            {rows.length} partie(s) intéressée(s) · ISO 9001:4.2 · enregistrement via « Sauvegarder
            » uniquement
          </p>
          {hasUnsavedChanges && (
            <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
              <AlertTriangle className="h-3 w-3" />
              Modifications non sauvegardées
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!readOnly ? <DocumentSubmitButton document={document} /> : null}
          <DocumentRightsButton
            document={document}
            autoOpenDocumentRights={autoOpenDocumentRights}
          />
          {!readOnly ? (
            <>
              <button
                type="button"
                onClick={fillWithDemoData}
                disabled={demoButtonDisabled}
                title={
                  demoButtonDisabled
                    ? 'Chargement des sites ou des modules pour préremplir les cases…'
                    : undefined
                }
                className="flex items-center gap-2 rounded-lg border border-violet-200 bg-violet-50 px-4 py-2 text-sm font-medium text-violet-700 transition-colors hover:bg-violet-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Wand2 className="h-4 w-4" />
                Démo
              </button>
              {rows.length > 0 && (
                <button
                  type="button"
                  onClick={saveAllRows}
                  disabled={isSavingAll}
                  className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:opacity-60 ${
                    saveAllStatus === 'saved'
                      ? 'bg-emerald-500 text-white'
                      : saveAllStatus === 'error'
                        ? 'bg-red-500 text-white'
                        : hasUnsavedChanges
                          ? 'bg-amber-500 text-white hover:bg-amber-600'
                          : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                  }`}
                >
                  <Save className="h-4 w-4" />
                  {saveAllStatus === 'saving'
                    ? 'Sauvegarde…'
                    : saveAllStatus === 'saved'
                      ? 'Sauvegardé !'
                      : saveAllStatus === 'error'
                        ? 'Erreur'
                        : 'Sauvegarder'}
                </button>
              )}
            </>
          ) : null}
        </div>
      </div>

      <div className="space-y-2">
        {rows.map((row, index) => {
          const rid = typeof row.id === 'string' ? row.id : '';
          const hl = rid && changedValueIds?.has(rid);
          const validationRow =
            validationReview?.canValidate && rid
              ? validationReview.validations.find((v) => v.documentValueId === rid)
              : undefined;
          return (
            <div
              key={String(row.id)}
              className={`rounded-xl border border-slate-200 bg-white overflow-hidden ${
                hl ? 'ring-2 ring-amber-300/90 shadow-sm' : ''
              }`}
            >
              <div className="flex items-center gap-3 px-4 py-3">
                <button
                  type="button"
                  className="flex flex-1 items-center gap-3 text-left"
                  onClick={() => setOpenAccordion(openAccordion === index ? null : index)}
                >
                  <span className="text-xs font-bold text-slate-400 w-6">{index + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="truncate text-sm font-medium text-slate-900">
                      {row.designation || (
                        <span className="italic text-slate-400">Sans désignation</span>
                      )}
                    </p>
                    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                      <span
                        className={`${BADGE_BASE} ${categoryBadgeClass(row.categorie)}`}
                        title="Catégorie"
                      >
                        {formatCategorieLabel(row.categorie)}
                      </span>
                      <span
                        className={`${BADGE_BASE} ${impactBadgeClass(row.impact)}`}
                        title="Impact"
                      >
                        Impact : {formatImpactLabel(row.impact)}
                      </span>
                      <span
                        className={`${BADGE_BASE} border-slate-200 bg-white text-slate-700`}
                        title="Sites associés"
                      >
                        <Building2 className="h-3 w-3 shrink-0 opacity-70" aria-hidden />
                        {sitesLabel(row.sites?.length ?? 0)}
                      </span>
                      <span
                        className={`${BADGE_BASE} border-slate-200 bg-white text-slate-700`}
                        title="Modules ISO associés"
                      >
                        <Layers className="h-3 w-3 shrink-0 opacity-70" aria-hidden />
                        {modulesLabel(row.modules?.length ?? 0)}
                      </span>
                    </div>
                  </div>
                  {openAccordion === index ? (
                    <ChevronUp className="h-4 w-4 text-slate-400" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-slate-400" />
                  )}
                </button>
                {!readOnly ? (
                  <button
                    type="button"
                    onClick={() => deleteRow(index)}
                    className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-500"
                    title="Supprimer"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                ) : null}
              </div>

              {validationRow && validationReview ? (
                <InlineDocumentValueValidation
                  documentId={document.id}
                  canValidate={validationReview.canValidate}
                  validationRow={validationRow}
                />
              ) : null}

              {openAccordion === index && (
                <div
                  className={`border-t border-slate-100 px-4 pb-4 pt-3 grid grid-cols-2 gap-4 ${readOnly ? 'pointer-events-none select-none opacity-[0.96]' : ''}`}
                >
                  <label className="flex flex-col gap-1 text-xs font-medium text-slate-600">
                    Désignation *
                    <input
                      type="text"
                      className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-normal outline-none focus:border-emerald-500"
                      value={row.designation}
                      onChange={(e) => updateRow(index, { designation: e.target.value })}
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-xs font-medium text-slate-600">
                    Catégorie
                    <select
                      className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-normal outline-none focus:border-emerald-500 bg-white"
                      value={row.categorie}
                      onChange={(e) => updateRow(index, { categorie: e.target.value })}
                    >
                      {REGISTRY_CATEGORIES.map((c) => (
                        <option key={c.value} value={c.value}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="col-span-2 flex flex-col gap-1 text-xs font-medium text-slate-600">
                    Besoins & attentes
                    <textarea
                      className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-normal outline-none focus:border-emerald-500 min-h-[60px] resize-y"
                      value={row.besoinsEtAttentes}
                      onChange={(e) => updateRow(index, { besoinsEtAttentes: e.target.value })}
                    />
                  </label>
                  <label className="col-span-2 flex flex-col gap-1 text-xs font-medium text-slate-600">
                    Exigences applicables
                    <textarea
                      className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-normal outline-none focus:border-emerald-500 min-h-[60px] resize-y"
                      value={row.exigences}
                      onChange={(e) => updateRow(index, { exigences: e.target.value })}
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-xs font-medium text-slate-600">
                    Impact
                    <select
                      className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-normal outline-none focus:border-emerald-500 bg-white"
                      value={row.impact}
                      onChange={(e) => updateRow(index, { impact: e.target.value })}
                    >
                      {REGISTRY_IMPACTS.map((c) => (
                        <option key={c.value} value={c.value}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="flex flex-col gap-1 text-xs font-medium text-slate-600">
                    Pilote
                    <input
                      type="text"
                      className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-normal outline-none focus:border-emerald-500"
                      value={row.pilote}
                      onChange={(e) => updateRow(index, { pilote: e.target.value })}
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-xs font-medium text-slate-600">
                    Dernière revue
                    <input
                      type="date"
                      className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-normal outline-none focus:border-emerald-500"
                      value={toInputDate(row.dateDerniereRevue)}
                      onChange={(e) => updateRow(index, { dateDerniereRevue: e.target.value })}
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-xs font-medium text-slate-600">
                    Prochaine revue
                    <input
                      type="date"
                      className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-normal outline-none focus:border-emerald-500"
                      value={toInputDate(row.dateProchaineRevue)}
                      onChange={(e) => updateRow(index, { dateProchaineRevue: e.target.value })}
                    />
                  </label>
                  <label className="col-span-2 flex flex-col gap-1 text-xs font-medium text-slate-600">
                    Modalités de prise en compte
                    <textarea
                      className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-normal outline-none focus:border-emerald-500 min-h-[60px] resize-y"
                      value={row.modalitesPriseEnCompte}
                      onChange={(e) => updateRow(index, { modalitesPriseEnCompte: e.target.value })}
                    />
                  </label>
                  <label className="col-span-2 flex flex-col gap-1 text-xs font-medium text-slate-600">
                    Commentaires
                    <textarea
                      className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-normal outline-none focus:border-emerald-500 min-h-[60px] resize-y"
                      value={row.commentaires}
                      onChange={(e) => updateRow(index, { commentaires: e.target.value })}
                    />
                  </label>

                  <div className="col-span-2 space-y-2 rounded-lg border border-slate-100 bg-slate-50/80 p-3">
                    <div>
                      <p className="text-xs font-medium text-slate-700">Sites concernés</p>
                      <p className="mt-0.5 text-[11px] text-slate-500">
                        Cochez un ou plusieurs sites concernés (obligatoire avant « Sauvegarder »).
                        {document.strictValidation
                          ? ' Les autres champs sont aussi validés strictement.'
                          : ''}
                      </p>
                    </div>
                    {sitesLoading ? (
                      <p className="text-xs text-slate-500">Chargement des sites…</p>
                    ) : !workSites?.length ? (
                      <p className="text-xs text-amber-700">
                        Aucun site assigné à votre compte. Demandez à un administrateur de vous
                        affecter à un site.
                      </p>
                    ) : (
                      <div className="max-h-36 space-y-1.5 overflow-y-auto rounded-md border border-slate-200 bg-white p-2">
                        {workSites.map((site) => {
                          const checked = row.sites.some((s) => s.id === site.siteId);
                          return (
                            <label
                              key={site.siteId}
                              className="flex cursor-pointer items-start gap-2 rounded px-1 py-0.5 text-sm text-slate-700 hover:bg-slate-50"
                            >
                              <input
                                type="checkbox"
                                className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-emerald-600"
                                checked={checked}
                                onChange={(e) => toggleRowSite(index, site, e.target.checked)}
                              />
                              <span className="font-normal">
                                {site.siteName}
                                {site.city ? (
                                  <span className="text-slate-500"> · {site.city}</span>
                                ) : null}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="col-span-2 space-y-2 rounded-lg border border-slate-100 bg-slate-50/80 p-3">
                    <div>
                      <p className="text-xs font-medium text-slate-700">Modules ISO concernés</p>
                      <p className="mt-0.5 text-[11px] text-slate-500">
                        Optionnel — parmi les modules ISO déjà assignés à vos sites (voir page
                        Modules).
                      </p>
                    </div>
                    {!workSites?.length ? (
                      <p className="text-xs text-slate-500">
                        Ajoutez d’abord des sites à votre compte pour lister les modules concernés.
                      </p>
                    ) : modulesLoading ? (
                      <p className="text-xs text-slate-500">Chargement des modules…</p>
                    ) : !moduleChoices.length ? (
                      <p className="text-xs text-slate-500">
                        Aucun module ISO assigné à vos sites. Configurez-les dans la page Modules.
                      </p>
                    ) : (
                      <div className="max-h-40 space-y-1.5 overflow-y-auto rounded-md border border-slate-200 bg-white p-2">
                        {moduleChoices.map((mod) => {
                          const checked = row.modules.some((m) => m.id === mod.id);
                          return (
                            <label
                              key={mod.id}
                              className="flex cursor-pointer items-start gap-2 rounded px-1 py-0.5 text-sm text-slate-700 hover:bg-slate-50"
                            >
                              <input
                                type="checkbox"
                                className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-emerald-600"
                                checked={checked}
                                onChange={(e) => toggleRowModule(index, mod, e.target.checked)}
                              />
                              <span className="font-normal">
                                <span className="font-medium text-slate-800">{mod.code}</span>
                                <span className="text-slate-600"> — {mod.name}</span>
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {rows.length === 0 && (
        <p className="mt-6 text-center text-sm text-slate-500">
          Aucune partie intéressée identifiée.
        </p>
      )}

      {!readOnly ? (
        <button
          type="button"
          onClick={addRow}
          className={`flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-200 bg-white px-4 py-3.5 text-sm font-medium text-slate-600 shadow-sm transition-colors hover:border-emerald-400 hover:bg-emerald-50/60 hover:text-emerald-800 ${rows.length === 0 ? 'mt-3' : 'mt-2'}`}
        >
          <Plus className="h-4 w-4 shrink-0" />
          Ajouter une partie intéressée
        </button>
      ) : null}
    </div>
  );
}
